import { ColTypeDef, ValueFormatterParams, ValueParserParams } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import i18next from 'i18next'
import {
  SelectCellEditor,
  NumericCellEditor,
  PercentageCellEditor,
  DateRangeCellEditor,
  ColorPickerEditor,
  ScenarioCascaderEditor,
  DimensionTreeSelectEditor,
  DateCellEditor
} from './Editor'
import {
  SelectCellRenderer,
  DateRangeCellRenderer,
  ColorCellRenderer,
  ScenarioCascaderRenderer,
  DimensionTreeSelectRenderer
} from './cellRenderers'
import DateColumnFilter from './Filter/DateColumnFilter'
import DateRangeColumnFilter from './Filter/DateRangeColumnFilter'
import { parseDate } from '../../../../config/dayjsConfig'
import { formatVariableValue } from '../../../../pages/reporting/custom/components/grid/utils/valueFormatting'
import { buildGridNumberFormat, getDecimalsFromParams } from './gridUtils'
import { VariableType } from '../../../../components/Table/types'

/**
 * Format grid cell value using NumberFormatConfig (same model as Custom Reports).
 * Builds format config from row data and column config, then delegates to formatVariableValue.
 */
export const formatGridValue = (params: ValueFormatterParams, variableType?: VariableType): string => {
  if (params.value == null) return ''

  const { numberFormat, isPercentage } = buildGridNumberFormat(params, variableType)
  // buildGridNumberFormat resolves its own effective type (param/row/default fallback)
  // but only exposes isPercentage; currency is never passed here so reconstructing is safe.
  const resolvedType = isPercentage ? VariableType.percentage : VariableType.absolute
  return formatVariableValue(params.value, numberFormat, resolvedType)
}

/**
 * Format percentage value - delegates to formatGridValue
 * Exported for testing and backwards compatibility
 */
export const formatPercentageValue = (params: ValueFormatterParams): string => {
  return formatGridValue(params, VariableType.percentage)
}

/**
 * Format numeric value - delegates to formatGridValue
 * Exported for testing and backwards compatibility
 */
export const formatNumericValue = (params: ValueFormatterParams): string => {
  return formatGridValue(params, VariableType.absolute)
}

/**
 * Parse percentage string ("50%" -> 0.5)
 * Uses proper decimal precision based on row/column config
 * Exported for testing
 */
export const parsePercentageValue = (params: ValueParserParams): number | null => {
  const { newValue } = params

  // Get the correct decimal precision for percentage values
  const decimals = getDecimalsFromParams(params, VariableType.percentage)
  // Storage decimals = display decimals + 2 (e.g., 55.3% = 1 decimal → store as 0.553 = 3 decimals)
  const storageDecimals = decimals + 2

  if (typeof newValue === 'number') {
    // Round to avoid floating point issues
    return Number((newValue as number).toFixed(storageDecimals))
  }
  if (newValue == null) return null

  if (typeof newValue === 'string') {
    const parsed = parseFloat(newValue.replace(/[%\s]/g, '').replace(',', '.'))
    if (Number.isNaN(parsed)) return null
    // Convert from percentage display to decimal (50 -> 0.5)
    const decimal = parsed / 100
    // Round with proper precision
    return Number(decimal.toFixed(storageDecimals))
  }

  return newValue as any
}

/**
 * Parse numeric string with comma/dot support
 * Rounds to specified decimal places using type-based defaults
 * Exported for testing
 */
export const parseNumericValue = (params: ValueParserParams): number | null => {
  const { newValue } = params

  // Use shared decimals logic (type-based defaults)
  const decimals = getDecimalsFromParams(params, VariableType.absolute)

  if (typeof newValue === 'number') {
    // Round to avoid floating point issues and limit decimals
    return Number((newValue as number).toFixed(decimals))
  }
  if (newValue == null || newValue === '') return null

  if (typeof newValue === 'string') {
    const cleaned = newValue.replace(/\s/g, '').replace(',', '.')
    const parsed = parseFloat(cleaned)
    if (Number.isNaN(parsed)) return null
    // Round to specified decimals
    return Number(parsed.toFixed(decimals))
  }

  return newValue as any
}

/**
 * Custom column type definitions for ag-Grid.
 * These provide reusable configurations for cell editors, renderers, and formatting.
 *
 * Usage:
 * ```typescript
 * {
 *   field: 'status',
 *   type: 'select',
 *   cellEditorParams: { options: [...] }
 * }
 * ```
 */
export const customColumnTypes: { [key: string]: ColTypeDef } = {
  /**
   * Select dropdown column type
   * - Shows dropdown icon as visual indicator
   * - Uses SelectCellEditor for editing
   * - Editable by default
   *
   * Default params can be overridden in column definition:
   * ```typescript
   * {
   *   type: 'select',
   *   cellEditorParams: {
   *     options: myOptions,
   *     valueListMaxHeight: 300
   *   }
   * }
   * ```
   */
  select: {
    cellEditor: SelectCellEditor,
    cellRenderer: SelectCellRenderer,
    editable: true,
    cellEditorParams: {
      // Default params - can be overridden in column definition
      valueListMaxHeight: 200
    },
    valueFormatter: params => {
      // Access options from cellEditorParams
      const options = params.colDef?.cellEditorParams?.options
      if (!options || !Array.isArray(options)) return params.value

      const option = options.find((opt: any) => opt.value === params.value)
      return option?.label || params.value
    },
    valueParser: params => {
      // Access options from cellEditorParams
      const options = params.colDef?.cellEditorParams?.options
      if (!options || !Array.isArray(options)) return params.newValue

      // If already the correct value type, return as is
      const option = options.find((opt: any) => opt.value === params.newValue)
      if (option) return params.newValue

      // Otherwise find by label
      const optionByLabel = options.find((opt: any) => opt.label === params.newValue)
      return optionByLabel?.value || params.newValue
    }
  },

  /**
   * Percentage column type
   * - Formats values as percentages (0.1 -> "10.0%")
   * - Uses PercentageCellEditor for editing
   * - Right-aligned by default
   * - Handles fill handle by parsing percentage strings (supports both comma and dot as decimal separator)
   *
   * Default params can be overridden in column definition:
   * ```typescript
   * {
   *   type: 'percentage',
   *   cellEditorParams: {
   *     allowNegative: true,
   *     minValue: -1,
   *     maxValue: 10
   *   }
   * }
   * ```
   */
  percentage: {
    cellEditor: PercentageCellEditor,
    cellClass: 'ag-right-aligned-cell',
    editable: true,
    cellEditorParams: {
      // Default params - can be overridden in column definition
      allowNegative: false,
      minValue: 0,
      maxValue: 1
    },
    useValueParserForImport: false,
    valueFormatter: formatPercentageValue,
    valueParser: parsePercentageValue
  },

  /**
   * Numeric column type with custom editor
   * - Uses NumericCellEditor for editing
   * - Right-aligned by default
   * - Formats numbers with configurable decimal places
   *
   * Default params can be overridden in column definition:
   * ```typescript
   * {
   *   type: 'numeric',
   *   cellEditorParams: {
   *     decimals: 0,           // Number of decimal places (default: 0 for absolute type)
   *     allowNegative: true,   // Allow negative values (default: true)
   *     min: 0,                // Minimum value (optional)
   *     max: 1000000           // Maximum value (optional)
   *   }
   * }
   * ```
   */
  numeric: {
    cellEditor: NumericCellEditor,
    cellClass: 'ag-right-aligned-cell',
    editable: true,
    cellEditorParams: {
      // Default params - can be overridden in column definition
      decimals: 0, // Type-based default for absolute values
      allowNegative: true
    },
    valueFormatter: formatNumericValue,
    valueParser: parseNumericValue
  },

  /**
   * Data type column type
   * - Displays data type with translation (budget, actuals, forecast)
   * - Uses i18next to translate values from global namespace
   * - Non-editable by default
   *
   * Expected values: 'budget', 'actuals', 'forecast'
   * Translation keys: 'global:budget', 'global:actuals', 'global:forecast'
   *
   * Usage:
   * ```typescript
   * {
   *   field: 'dataType',
   *   type: 'dataType'
   * }
   * ```
   */
  dataType: {
    editable: false,
    valueFormatter: params => {
      if (!params.value) return '-'
      // Translate using global namespace (force string return)
      const translated = i18next.t(`global:${params.value}`, params.value)
      return typeof translated === 'string' ? translated : String(translated)
    }
  },

  /**
   * Date range column type
   * - Shows date range picker for selecting start and end periods
   * - Displays formatted date range (MM/YYYY - MM/YYYY) with calendar icon
   * - Uses popup editor by default
   *
   * Value format:
   * ```typescript
   * {
   *   startPeriod: { year: number, month: number },
   *   endPeriod: { year: number, month: number }
   * }
   * ```
   */
  datePicker: {
    cellEditor: DateCellEditor,
    cellClass: 'ag-right-aligned-cell',
    editable: true,
    filter: {
      component: DateRangeColumnFilter,
      handler: 'agDateColumnFilterHandler'
    },
    filterParams: {
      dateComponent: DateColumnFilter,
      // ag-grid's inRange is exclusive by default — the picked start and end days would drop out.
      inRangeInclusive: true,
      comparator: (filterDate: Date, cellValue: string) => {
        if (!cellValue) return -1
        const cellDate = parseDate(cellValue)
        if (!cellDate) return -1
        const cellTimestamp = cellDate.startOf('day').valueOf()
        const filterTimestamp = dayjs(filterDate).startOf('day').valueOf()
        if (cellTimestamp < filterTimestamp) return -1
        if (cellTimestamp > filterTimestamp) return 1
        return 0
      }
    },
    valueFormatter: params => {
      if (!params.value) return ''
      return parseDate(params.value)?.format('DD.MM.YYYY') || ''
    }
  },

  dateRange: {
    cellEditor: DateRangeCellEditor,
    cellRenderer: DateRangeCellRenderer,
    cellEditorPopup: true,
    editable: true
  },

  /**
   * Color column type
   * - Displays color swatch when not editing
   * - Opens Ant Design ColorPicker when cell is clicked
   * - Stores values as hex strings (e.g., "#FF0000")

   * Usage:
   * ```typescript
   * {
   *   field: 'color',
   *   type: 'color',
   *   editable: true // or callback function
   * }
   * ```
   */
  color: {
    cellRenderer: ColorCellRenderer,
    cellEditor: ColorPickerEditor,
    editable: true,
    suppressKeyboardEvent: params => {
      // Keep editor open when user interacts with ColorPicker
      return params.editing
    }
  },

  /**
   * Scenario Cascader column type
   * - Displays scenario selection with dropdown icon
   * - Opens scenario cascader when cell is clicked
   * - Requires companyId in cellEditorParams
   * - Supports fill handle - copies array values directly
   *
   * Usage:
   * ```typescript
   * {
   *   field: 'dataType',
   *   type: 'scenarioCascader',
   *   editable: true,
   *   cellEditorParams: {
   *     companyId: 'company-id'
   *   }
   * }
   * ```
   */
  scenarioCascader: {
    cellRenderer: ScenarioCascaderRenderer,
    cellEditor: ScenarioCascaderEditor,
    editable: true,
    cellStyle: { overflow: 'visible' },
    useValueFormatterForExport: false,
    valueParser: params => {
      const { newValue } = params
      // Already an array - pass through
      if (Array.isArray(newValue)) {
        return newValue
      }
      // Try to parse JSON string (in case fill handle stringified it)
      if (typeof newValue === 'string') {
        try {
          const parsed = JSON.parse(newValue)
          if (Array.isArray(parsed)) {
            return parsed
          }
        } catch {
          // Not JSON, return as string (e.g. 'actuals')
        }
        return newValue
      }
      return newValue
    }
  },

  /**
   * Dimension Tree Select column type
   * - Displays dimension selection with dropdown icon
   * - Opens dimension tree select when cell is clicked
   * - Requires companyId in cellEditorParams
   *
   * Usage:
   * ```typescript
   * {
   *   field: 'dimensionId',
   *   type: 'dimensionTreeSelect',
   *   editable: true,
   *   cellEditorParams: {
   *     companyId: 'company-id'
   *   }
   * }
   * ```
   */
  dimensionTreeSelect: {
    cellRenderer: DimensionTreeSelectRenderer,
    cellEditor: DimensionTreeSelectEditor,
    editable: true,
    cellStyle: { overflow: 'visible' }
  }
}
